import React from 'react';
import { IHTCalculation, EstateAssets, EstateProfile } from '../../types';
import { resolveResidenceForConsumers } from '../../server/services/rnrbResidenceFacts';
import { RegulationReference } from './RegulationReference';
import { getAuthority } from '../../server/rules/authorities';
import { selectReportPanels } from './selectReportPanels';

interface Props {
    ihtCalc?: IHTCalculation | null;
    assets?: EstateAssets;
    /** Residence-facts mode and nomination; omitted = an unstamped (legacy) estate. */
    ihtDetails?: EstateProfile['iht_details'];
}

type RnrbReason = {
    label: string;
    met: boolean;
};

function getRnrbDetails(
    ihtCalc: IHTCalculation | null | undefined,
    assets: EstateAssets | undefined,
    ihtDetails?: EstateProfile['iht_details'],
): { applied: boolean; amount: number; reasons: RnrbReason[]; notAppliedReason: string | null } {
    const b = ihtCalc?.breakdown;
    const rnrbAvailable = b?.rnrb_available ?? 0;
    const rnrbBeforeTaper = b?.rnrb_before_taper ?? 0;
    const taperAmount = b?.taper_amount ?? 0;
    // Use taper base (after debts, before exemptions/reliefs) for display
    const taperBaseValue = b?.rnrb_taper_base ?? b?.net_value ?? b?.gross_value ?? 0;

    // Check if RNRB is applied (either before or after taper)
    const applied = rnrbAvailable > 0 || rnrbBeforeTaper > 0;
    // The residence state is the shared Stage-1 resolution (the engine's resolver), never is_main_residence. Nothing
    // here recalculates the RNRB: the amounts are the engine's. Without the asset list nothing is said about it.
    const residence = assets ? resolveResidenceForConsumers(assets.properties, ihtDetails) : null;

    if (applied && residence?.noCurrentRpi) {
        // s.8FB: no residence in the estate at death; the amount is the downsizing addition alone.
        const reasons: RnrbReason[] = [
            { label: 'No residence in the estate at death — the RNRB comes from a former home (downsizing addition)', met: true },
            {
                label: taperBaseValue > 2_000_000
                    ? `Estate value for taper (${fmt(taperBaseValue)}) exceeds £2,000,000 threshold`
                    : `Estate value for taper (${fmt(taperBaseValue)}) below £2,000,000 threshold`,
                met: taperBaseValue <= 2_000_000,
            },
        ];
        return { applied: true, amount: rnrbAvailable, reasons, notAppliedReason: null };
    }

    if (applied) {
        const reasons: RnrbReason[] = [
            {
                label: 'Main residence passes to direct descendants',
                met: true,
            },
            {
                label: taperBaseValue > 2_000_000
                    ? `Estate value for taper (${fmt(taperBaseValue)}) exceeds £2,000,000 threshold`
                    : `Estate value for taper (${fmt(taperBaseValue)}) below £2,000,000 threshold`,
                met: taperBaseValue <= 2_000_000,
            },
            residence?.provisional
                ? { label: 'Property used provisionally as the residence — not yet confirmed that they lived in it', met: false }
                : { label: 'Property qualifies as residence', met: true },
        ];

        return { applied: true, amount: rnrbAvailable, reasons, notAppliedReason: null };
    }

    // Not applied — determine the reason
    const blockers = ihtCalc?.blockers ?? [];
    let notAppliedReason: string | null = null;

    // Check for taper wipeout
    if (rnrbBeforeTaper > 0 && taperAmount >= rnrbBeforeTaper) {
        notAppliedReason = `Estate value exceeds the £2,000,000 taper threshold — the full RNRB entitlement of ${fmt(rnrbBeforeTaper)} has been tapered away.`;
    }
    // Check if no qualifying residence
    else if (blockers.includes('MISSING_CLOSELY_INHERITED_RESIDENCE_VALUE')) {
        notAppliedReason = 'No qualifying residence details have been provided. RNRB requires a main home passing to direct descendants.';
    }
    // Check property destination
    else if (residence?.nominationRequired) {
        notAppliedReason = 'More than one property may count as the residence, and a qualifying residence has not yet been nominated. Only one can count, and the executors or administrators choose which; until then the RNRB is withheld.';
    }
    else if (residence?.residenceHistoryUnknown) {
        notAppliedReason = 'It is not yet known whether they lived in each property as their home, so the residence for the RNRB cannot be identified yet.';
    }
    else if (residence?.noCurrentRpi && (assets?.properties?.length ?? 0) > 0) {
        notAppliedReason = 'No property in the estate at death is recorded as one they lived in as their home. The RNRB can then only come from a former home they sold or gave away (downsizing).';
    }
    else {
        const mainHome = residence?.qri ?? null;
        if (!mainHome) {
            notAppliedReason = 'No qualifying residence identified in the estate. The RNRB requires a main home to be included.';
        } else if (mainHome.ownership_type === 'joint_tenants' && mainHome.joint_owner_relationship === 'spouse') {
            notAppliedReason = 'The main residence passes to the surviving spouse by right of survivorship, not to direct descendants. The unused RNRB may be transferable to the surviving spouse\'s estate.';
        } else {
            // Generic — property not left to descendants
            notAppliedReason = 'The main residence does not appear to pass to direct descendants (children, grandchildren, etc.). The RNRB is only available when a qualifying home is inherited by lineal descendants.';
        }
    }

    return { applied: false, amount: 0, reasons: [], notAppliedReason };
}

function fmt(n: number): string {
    return n.toLocaleString('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 });
}

export const RnrbExplanation: React.FC<Props> = ({ ihtCalc, assets, ihtDetails }) => {
    if (!ihtCalc) return null;

    // The RNRB did not exist before 6 Apr 2017, so it never shows for an
    // out-of-range death (pre-IHT or pre-2017) — the selector raises a banner for
    // both and sets showRnrb=false. We suppress on showBanner rather than the
    // narrower showRnrb so that on *supported* dates the existing applied / fully-
    // tapered / not-applied explanations are all preserved.
    const panels = selectReportPanels(ihtCalc);
    if (panels.showBanner) return null;

    const { applied, amount, reasons, notAppliedReason } = getRnrbDetails(ihtCalc, assets, ihtDetails);
    const b = ihtCalc.breakdown;
    const taperAmount = b?.taper_amount ?? 0;
    const rnrbBeforeTaper = b?.rnrb_before_taper ?? 0;
    const downsizing = b?.downsizing_addition ?? 0;

    // Cite the RNRB authority in force at the date of death (null before 6 Apr 2017).
    const dod = b?.date_of_death ?? null;
    const rnrbAuthority = getAuthority('RNRB', dod);

    // Don't show if RNRB is completely irrelevant (no property at all)
    if (!applied && !notAppliedReason) return null;

    return (
        <div className="bg-white border border-[#E8E4DB] p-4">
            <h3 className="font-serif text-[18px] font-medium text-[#1A1A1A] m-0 mb-2">
                Residence Nil Rate Band
            </h3>

            {applied ? (
                <>
                    <p className="font-serif italic text-[13px] text-[#3F3D39] leading-relaxed mb-3">
                        <span className="font-mono not-italic font-medium text-[#3730A3]">{fmt(amount)}</span> applied to this estate.
                    </p>

                    {/* Taper detail */}
                    {taperAmount > 0 && rnrbBeforeTaper > 0 && (
                        <p className="text-[13px] text-[#3F3D39] mb-2 leading-relaxed">
                            RNRB before taper: <span className="font-mono tabular-nums">{fmt(rnrbBeforeTaper)}</span> — reduced by <span className="font-mono tabular-nums">{fmt(taperAmount)}</span> due to estate exceeding £2,000,000 threshold.
                        </p>
                    )}

                    {/* TRNRB detail */}
                    {b?.tnrb_detail && b.tnrb_detail.rnrb_transferred_percent > 0 && (
                        <div className="text-[13px] text-[#3F3D39] mb-2 space-y-0.5 leading-relaxed">
                            <p>Base RNRB: <span className="font-mono tabular-nums">{fmt(b.tnrb_detail.rnrb_base)}</span></p>
                            <p>Transferred RNRB: <span className="font-mono tabular-nums">{fmt(b.tnrb_detail.rnrb_transferred_amount)}</span> ({b.tnrb_detail.rnrb_transferred_percent}% unused from predeceased spouse)</p>
                            <p>Combined RNRB: <span className="font-mono tabular-nums">{fmt(b.tnrb_detail.combined_rnrb_max)}</span></p>
                        </div>
                    )}

                    {/* Downsizing detail */}
                    {downsizing > 0 && (
                        <p className="text-[13px] text-[#3F3D39] mb-2 leading-relaxed">
                            Includes <span className="font-mono tabular-nums">{fmt(downsizing)}</span> downsizing addition (former qualifying residence).
                        </p>
                    )}

                    <div className="space-y-1 mt-3">
                        <p className="text-[10px] tracking-[0.18em] uppercase text-[#6B6762] mb-1">Eligibility</p>
                        {reasons.map((r, i) => (
                            <div key={i} className="flex items-baseline gap-1.5 text-[13px]">
                                <span className={`font-serif shrink-0 ${r.met ? 'text-[#3730A3]' : 'text-[#7A5A00]'}`}>
                                    {r.met ? '✓' : '×'}
                                </span>
                                <span className={r.met ? 'text-[#3F3D39]' : 'text-[#7A5A00]'}>{r.label}</span>
                            </div>
                        ))}
                    </div>
                </>
            ) : (
                <>
                    <p className="font-serif italic text-[13px] text-[#6B6762] mb-2">Not applied to this estate.</p>
                    <p className="text-[13px] text-[#3F3D39] leading-relaxed">
                        {notAppliedReason}
                    </p>
                </>
            )}

            <p className="text-[11px] text-[#AFA99F] italic mt-3 pt-2 border-t border-[#E8E4DB]">
                The RNRB provides up to £175,000 additional tax-free allowance when a main residence passes to direct descendants.
            </p>

            {rnrbAuthority && (
                <RegulationReference
                    title={rnrbAuthority.label}
                    legislationReference={rnrbAuthority.legislation}
                    hmrcManualReference={rnrbAuthority.manual}
                    externalLink={rnrbAuthority.url}
                    asAtDate={dod}
                />
            )}
        </div>
    );
};
